import { YouTubeFacade } from "@/components/site/YouTubeFacade";
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, Calendar, Check, Send, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { motion, type Variants } from "framer-motion";
import { SiteLayout } from "@/components/site/Layout";
import { WelcomeVideo } from "@/components/sections/WelcomeVideo";
import { Testimonials } from "@/components/sections/Testimonials";
import { Appointment } from "@/components/sections/Appointment";
import { BookAppointmentLink } from "@/components/site/BookAppointmentLink";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import familyImg from "@/assets/family-dentistry-pickering-on.png.asset.json";
import gentleCareImg from "@/assets/family-dentist-pickering-gentle-care.png.asset.json";
import thumb1 from "@/assets/service-pediatric-240.webp";
import thumb2 from "@/assets/service-preventative-240.webp";
import thumb3 from "@/assets/service-xray-240.webp";
import ctaParallax from "@/assets/cta-parallax.jpg";
import { loadDashboardSeo, mergeSeoHead } from "@/lib/dashboard-seo";
import { getService } from "@/lib/dashboard";
import { dashboardServiceOr } from "@/components/services/DashboardServiceView";

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const } },
};

const relatedServices = [
  {
    to: "/services/general-dentistry/pediatric-dentistry-pickering-on" as const,
    hash: undefined as string | undefined,
    title: "Pediatric Dentistry",
    desc: "Gentle, kid-friendly dental care for healthy young smiles.",
    img: thumb1,
  },
  {
    to: "/services/preventive-dentistry/teeth-cleaning-pickering-on" as const,
    hash: undefined as string | undefined,
    title: "Teeth Cleaning",
    desc: "Professional cleanings to keep plaque and tartar in check.",
    img: thumb2,
  },
  {
    to: "/services/general-dentistry/emergency-dentist-pickering-on" as const,
    hash: undefined as string | undefined,
    title: "Emergency Dentist",
    desc: "Same-day appointments when you need urgent dental relief.",
    img: thumb3,
  },
];

const faqs = [
  {
    q: "What is family dentistry?",
    a: "Family dentistry provides dental care for patients at different life stages, including children, teens, adults, and seniors.",
  },
  {
    q: "Does Bayly Dental Clinic see children and adults?",
    a: "Yes. Bayly Dental Clinic provides care for children, teens, adults, seniors, first-time patients, and long-term patients.",
  },
  {
    q: "What services are included in family dentistry?",
    a: "Family dentistry may include routine exams, checkups, preventive care, restorative treatment, oral health monitoring, and coordinated care across family members.",
  },
  {
    q: "Can multiple family members visit the same dental clinic?",
    a: "Yes. Bayly Dental Clinic can treat multiple family members under one clinic, with care tailored to each person's needs. If you need a dentist for the whole family near me, our clinic is designed for household coordination.",
  },
  {
    q: "How does family dentistry help long-term oral health?",
    a: "It supports prevention, early detection, consistent records, age-based education, and ongoing care as dental needs change.",
  },
  {
    q: "Is Bayly Dental Clinic good for nervous children or anxious patients?",
    a: "The clinic uses a welcoming environment, clear communication, and a gentle approach for children and anxious patients.",
  },
  {
    q: "What should new patients expect at a family dental visit?",
    a: "New patients can expect their needs to be assessed individually, findings explained clearly, and next steps discussed before treatment proceeds.",
  },
  {
    q: "Does Bayly Dental Clinic explain treatment costs and next steps?",
    a: "Yes. The clinic explains next steps clearly and provides guidance based on each patient's needs, ensuring full transparency.",
  },
];

const PAGE_URL =
  "https://baylydentalclinic.com/services/general-dentistry/family-dentist-pickering-on";
const META_TITLE = "Family Dentist in Pickering | Bayly Dental Clinic";
const META_DESC =
  "Looking for a family dentist in Pickering? Bayly Dental Clinic provides dental care for children, teens, adults, and seniors. Book your visit today.";

const webPageSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalWebPage",
      "@id": `${PAGE_URL}#webpage`,
      url: PAGE_URL,
      name: "Family Dentistry in Pickering, ON | Bayly Dental Clinic",
      headline: "Family Dentist in Pickering, ON",
      description:
        "Family dentistry provides comprehensive oral healthcare tailored to patients at every stage of life, including children, teens, adults, and seniors. At Bayly Dental Clinic, we offer coordinated family care in Pickering.",
      inLanguage: "en-CA",
      keywords: [
        "family dentist in pickering",
        "family dentistry in pickering",
        "dentist for families in pickering",
        "family dental clinic pickering",
        "dentist in pickering accepting new patients",
        "dental care for children teens adults and seniors",
      ],
      medicalAudience: "Patient",
      provider: {
        "@type": "MedicalOrganization",
        name: "Bayly Dental Clinic",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1635 Bayly St Building F, Unit 3",
          addressLocality: "Pickering",
          addressRegion: "ON",
          postalCode: "L1W 3N2",
          addressCountry: "CA",
        },
        telephone: "+1-905-492-1234",
      },
    },
    {
      "@type": "FAQPage",
      "@id": `${PAGE_URL}#faq`,
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export const Route = createFileRoute(
  "/services_/general-dentistry_/family-dentist-pickering-on",
)({
  loader: async () => { const [seo, svc] = await Promise.all([loadDashboardSeo("/services/general-dentistry/family-dentist-pickering-on"), getService("/services/general-dentistry/family-dentist-pickering-on")]); return { seo, service: svc?.data?.published_payload ?? null }; },
  head: ({ loaderData }) => mergeSeoHead(loaderData?.seo, ({
    meta: [
      { title: META_TITLE },
      { name: "description", content: META_DESC },
      { property: "og:title", content: META_TITLE },
      { property: "og:description", content: META_DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: familyImg.url },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(webPageSchema) },
    ],
  })),
  component: dashboardServiceOr("/services_/general-dentistry_/family-dentist-pickering-on", FamilyDentistPage),
});

function SidebarBookingForm() {
  return (
    <div className="bg-card rounded-2xl p-5 shadow-[var(--shadow-card)] border border-border text-center">
      <span className="eyebrow">Quick Booking</span>
      <h3 className="mt-2 text-lg font-bold text-ink leading-snug">Book your appointment</h3>
      <p className="mt-1 text-xs text-muted-foreground">
        We'll get back to you within one business day.
      </p>
      <BookAppointmentLink className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-primary-foreground font-bold py-3 rounded-full shadow-md shadow-primary/20 transition-all hover:-translate-y-0.5 text-sm">
        <Calendar className="h-4 w-4" /> Book Appointment
      </BookAppointmentLink>
    </div>
  );
}

const keyTakeaways = [
  <><strong>Care for All Ages:</strong> Bayly Dental Clinic provides dental care for children, teens, adults, and seniors.</>,
  <><strong>Household Convenience:</strong> Multiple family members can receive care under one family dental clinic in Pickering.</>,
  <><strong>Comprehensive Services:</strong> Family dentistry includes routine exams, preventive care, restorative treatment, and oral health monitoring.</>,
  <><strong>Personalized Approach:</strong> Care is uniquely tailored to each patient's age, risk factors, and evolving dental needs.</>,
  <><strong>Patient-Focused Experience:</strong> The clinic emphasizes prevention, clear communication, comfort, and continuity of care for both first-time and long-term patients.</>,
];

const newPatientChecklist = [
  "A review of your dental and medical history",
  "A comprehensive oral examination",
  "A gum health and periodontal assessment",
  "Oral cancer screening (for adults)",
  "A bite and jaw evaluation",
  "An iTero 3D scan (when included in your exam package)",
  "Digital X-rays (only when clinically necessary)",
];

const clinicBenefits = [
  "Easier appointment planning for busy households",
  "Consistent dental records and thorough history tracking in one place",
  "A familiar environment that is especially helpful for children and anxious patients",
  "Better continuity of care as patients transition from childhood to adulthood",
  "Individual care plans designed specifically for each family member",
];

function FamilyDentistPage() {
  return (
    <SiteLayout>
      <section className="bg-surface">
        <div className="container-1120 py-10 md:py-14 text-center">
          <nav aria-label="breadcrumb" className="flex justify-center">
            <ol className="eyebrow flex flex-wrap items-center justify-center gap-2 bg-accent/70 text-ink/70 rounded-full px-5 py-2">
              <li>
                <Link to="/" className="hover:text-primary transition">Home</Link>
              </li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li>
                <Link to="/services" className="hover:text-primary transition">Services</Link>
              </li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li>
                <Link to="/services/general-dentistry" className="hover:text-primary transition">
                  General Dentistry
                </Link>
              </li>
              <ChevronRight className="h-3 w-3" aria-hidden="true" />
              <li aria-current="page" className="text-ink">Family Dentist</li>
            </ol>
          </nav>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold text-ink max-w-3xl mx-auto leading-tight">
            Family Dentist in Pickering, ON
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Bayly Dental Clinic offers family dentistry in Pickering for children, teens, adults,
            seniors, new patients, and long-term patients.
          </p>
        </div>
      </section>

      <section className="relative section-pad overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-soft)" }} />
        <div className="container-1120 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:items-start">
          <motion.article
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: "some" }}
            className="lg:col-span-2"
          >
            <div className="overflow-hidden rounded-3xl shadow-[var(--shadow-card)]">
              <img
                src={familyImg.url}
                alt="Multigenerational family smiling together after a family dentistry visit in Pickering, ON at Bayly Dental Clinic"
                title="Family Dentistry in Pickering, ON | Bayly Dental Clinic"
                data-caption="Three generations of patients welcomed for family dentistry in Pickering, ON at Bayly Dental Clinic."
                data-description="Family dentistry in Pickering, ON at Bayly Dental Clinic, a multigenerational family of patients, including a child, parents, and a grandparent, smiling together in our welcoming Pickering dental office. Our family dentists provide gentle, comprehensive oral health care for every stage of life, from a child's first dental visit to preventive cleanings, fillings, and restorative treatments for adults and seniors. Serving families across Pickering, Pickering, Whitby, and Oshawa, we make routine checkups and exams comfortable for the whole household. Book your family dentistry appointment in Pickering, ON today."
                width={1280}
                height={720}
                className="w-full h-auto object-cover aspect-[16/9]"
              />
            </div>

            <div id="service-content" className="mt-8 space-y-6 text-ink leading-relaxed">
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                <strong>Family dentistry</strong> provides comprehensive oral healthcare tailored to
                patients at every stage of life, including children, teens, adults, and seniors. It
                focuses on routine checkups, preventive treatments, and restorative care. At Bayly
                Dental Clinic in Pickering, we offer coordinated, gentle dental care to support
                your entire household in one welcoming location.
              </p>

              <h2 className="text-2xl md:text-3xl font-bold text-ink mt-10 leading-tight tracking-tight">
                Family Dental Care for Every Stage of Life
              </h2>
              <ul className="space-y-4 my-6">
                {keyTakeaways.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              <YouTubeFacade
                id="Ih92tB_NIeQ"
                title="Family Dentist at Bayly Dental Clinic"
                className="rounded-2xl shadow-[var(--shadow-card)]"
              />

              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Finding a trusted <strong>family dentist in Pickering</strong> is about more than
                booking separate appointments. Families often want one consistent dental home where
                children, parents, and seniors can receive age-appropriate care. At Bayly Dental
                Clinic, our <strong>family dentistry in Pickering</strong> is designed to support
                prevention, comfort, and clear communication across every stage of life.
              </p>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                What Is Family Dentistry?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Family dentistry provides dental care for patients of different ages, including
                children, teens, adults, and seniors, often within the same dental clinic.
              </p>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                For a broader overview of preventive habits, gum care, and lifelong oral wellness,
                the{" "}
                <a
                  href="https://www.cda-adc.ca/en/oral_health/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  Canadian Dental Association
                </a>{" "}
                offers trusted oral health resources for families of every age.
              </p>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Instead of moving between different practices as you grow, a{" "}
                <strong>dentist for families in Pickering</strong> offers ongoing dental care for
                the entire household. At Bayly Dental Clinic, we treat both first-time and
                long-term patients. We understand that care must be individualized, not
                one-size-fits-all, seamlessly coordinating preventive, restorative, and follow-up
                care to maintain healthy smiles year after year.
              </p>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                Who Can Visit a Family Dentist in Pickering?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Children, teens, adults, seniors, first-time patients, and long-term patients can
                all receive dedicated care at our clinic. If you are searching for a{" "}
                <strong>dentist in Pickering accepting new patients</strong>, our doors are open to
                your entire household.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-ink mt-8 leading-tight tracking-tight">
                Dental Care for Children and Teens
              </h3>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                A lifetime of healthy smiles begins with positive early experiences. For younger
                patients, we focus on routine{" "}
                <Link
                  to="/services/general-dentistry/dental-exam-pickering-on"
                  className="text-primary font-semibold hover:underline"
                >
                  Dental Exams
                </Link>
                , preventive care (like Dental Sealants and Fluoride Treatment), monitoring oral
                changes, and age-appropriate hygiene education. Our{" "}
                <Link
                  to="/services/general-dentistry/pediatric-dentistry-pickering-on"
                  className="text-primary font-semibold hover:underline"
                >
                  Pediatric Dentistry
                </Link>{" "}
                approach helps children build positive habits, while teens exploring smile
                alignment can learn more about{" "}
                <Link
                  to="/services/orthodontics"
                  className="text-primary font-semibold hover:underline"
                >
                  Orthodontics
                </Link>{" "}
                options suited to growing smiles.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-ink mt-8 leading-tight tracking-tight">
                Dental Care for Adults
              </h3>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Adult dental care shifts toward maintaining strong teeth and addressing wear or
                damage. We provide routine preventive visits, restorative treatments (such as{" "}
                <Link
                  to="/services/preventive-dentistry/tooth-fillings-pickering-on"
                  className="text-primary font-semibold hover:underline"
                >
                  Tooth Fillings
                </Link>
                ) when needed, and consistent monitoring of oral health changes over time. If a
                sudden toothache or injury arises between visits, our{" "}
                <Link
                  to="/services/general-dentistry/emergency-dentist-pickering-on"
                  className="text-primary font-semibold hover:underline"
                >
                  Emergency Dentistry
                </Link>{" "}
                team is here to help.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-ink mt-8 leading-tight tracking-tight">
                Dental Care for Seniors
              </h3>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                As we age, our mouths undergo unique changes. For seniors, we monitor changing
                dental needs, provide ongoing care, and offer solutions such as{" "}
                <Link
                  to="/services/restorative-dentistry/dental-implants-pickering-on"
                  className="text-primary font-semibold hover:underline"
                >
                  Dental Implants
                </Link>{" "}
                to restore long-term comfort and function so you can eat, speak, and smile with
                confidence.
              </p>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                What Services Are Included in Family Dentistry?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                <strong>Family dentistry Pickering</strong> services may include routine exams,
                checkups, preventive care, restorative treatments, oral health monitoring, and
                coordinated treatment planning.
              </p>
              <div className="overflow-x-auto rounded-2xl border border-border my-6">
                <table className="w-full text-left text-base">
                  <thead className="bg-accent/60">
                    <tr>
                      <th className="px-4 py-3 font-bold text-ink">Family Dentistry Service</th>
                      <th className="px-4 py-3 font-bold text-ink">How It Helps</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr><td className="px-4 py-3 font-semibold">Routine dental exams</td><td className="px-4 py-3 text-ink/80">Helps assess teeth, gums, bite, and oral health changes</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Preventive care</td><td className="px-4 py-3 text-ink/80">Supports cavity and gum disease prevention</td></tr>
                     <tr><td className="px-4 py-3 font-semibold"><Link to="/services/preventive-dentistry/dental-hygiene-pickering-on" className="text-primary hover:underline">Dental Hygiene</Link> and <Link to="/services/preventive-dentistry/teeth-cleaning-pickering-on" className="text-primary hover:underline">Teeth Cleaning</Link></td><td className="px-4 py-3 text-ink/80">Helps maintain oral health between exams</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Restorative treatments</td><td className="px-4 py-3 text-ink/80">Repairs teeth when issues such as cavities or damage are found</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Oral health monitoring</td><td className="px-4 py-3 text-ink/80">Tracks changes over time for each family member</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Coordinated family care</td><td className="px-4 py-3 text-ink/80">Helps families manage different needs in one clinic</td></tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                Why Is Having One Dental Clinic for the Whole Family Helpful?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                One family dental clinic can help simplify scheduling, maintain consistent records,
                and support continuity of care as each patient's needs change.
              </p>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                When you choose a <strong>family dental clinic Pickering</strong> residents trust,
                you benefit from:
              </p>
              <ul className="space-y-4 my-6">
                {clinicBenefits.map((item) => (
                  <li key={item} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                How Does Bayly Dental Clinic Help Children and Anxious Patients Feel Comfortable?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Bayly Dental Clinic uses a gentle approach, clear communication, and supportive
                care for children and anxious patients.
              </p>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Some patients need extra reassurance. When you search for a dentist near you for
                children and adults or a dentist for anxious children and adults, you want a team
                that prioritizes your peace of mind. As a{" "}
                <strong>gentle family dentist in Pickering</strong>, we focus on a supportive
                experience, clear explanations to parents and caregivers, and gentle communication
                so families know exactly what to expect.
              </p>

              <img
                src={gentleCareImg.url}
                alt="Gentle family dentist in Pickering, ON showing a young patient a tooth model while her mother smiles beside her at Bayly Dental Clinic"
                title="Gentle Family Dentist in Pickering, ON | Bayly Dental Clinic"
                data-caption="A gentle family dentist in Pickering, ON helping a young patient feel comfortable during her visit at Bayly Dental Clinic."
                data-description="Family dentist in Pickering, ON at Bayly Dental Clinic, a gentle dental hygienist sits chairside with a young patient and her mother, using a tooth model to explain treatment in a child-friendly way. This image illustrates how our family dentists help children and anxious patients feel calm and informed in our modern Pickering dental operatory, equipped with digital tooth charting and kid-friendly comforts. Serving families across Pickering, Pickering, Whitby, and Oshawa, we provide gentle, supportive family dentistry for every age. Book your family dentist appointment in Pickering, ON today."
                width={1280}
                height={720}
                loading="lazy"
                className="w-full h-auto object-cover aspect-[16/9] rounded-2xl shadow-[var(--shadow-soft)] mt-12"
              />

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                How Does Family Dentistry Support Long-Term Oral Health?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Family dentistry supports long-term oral health through prevention, early detection,
                education, and ongoing monitoring as dental needs change.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-ink mt-8 leading-tight tracking-tight">
                Prevention and Early Detection
              </h3>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Scheduling <strong>routine dental exams for families</strong> helps our team catch
                minor concerns like early decay or mild gum inflammation before they become more
                complex and require extensive restorative work.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-ink mt-8 leading-tight tracking-tight">
                Age-Based Oral Health Education
              </h3>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                We provide <strong>preventive dental care for families</strong> tailored to age and
                risk factors, ensuring parents know how to care for baby teeth and adults know how
                to protect their smiles as they age.
              </p>

              <h3 className="text-2xl md:text-3xl font-bold text-ink mt-8 leading-tight tracking-tight">
                Ongoing Care as Needs Change
              </h3>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Children, adults, and seniors have different dental priorities. Because we act as a{" "}
                <strong>dentist for kids and parents in Pickering</strong> under one roof, our care
                naturally evolves with you over time.
              </p>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                What Should New Patients Expect?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                New patients can expect their oral health needs to be assessed individually,
                findings explained clearly, and next steps discussed before treatment proceeds.
              </p>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                At a <strong>new patient family dentist Pickering</strong> appointment, we keep the
                process thorough yet comfortable. Your visit will generally include:
              </p>
              <ul className="space-y-4 my-6">
                {newPatientChecklist.map((item) => (
                  <li key={item} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                How Are Different Family Members' Treatments Coordinated?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Bayly Dental Clinic creates individual treatment plans for each patient and
                explains next steps clearly for families.
              </p>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Each patient receives their own customized plan. If your household needs a mix of
                services, preventive care can often be scheduled alongside other necessary
                treatments. Follow-ups or referrals are arranged when needed, making{" "}
                <strong>family dental checkups in Pickering</strong> highly efficient.
              </p>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                Why Choose Bayly Dental Clinic for Family Dentistry in Pickering?
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Bayly Dental Clinic provides family-friendly dental care with an emphasis on
                prevention, communication, flexible scheduling, and support for patients at
                different life stages.
              </p>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                As a local clinic serving our community, as well as acting as a convenient{" "}
                <strong>family dentist near Pickering</strong> and a{" "}
                <strong>family dentist near Markham</strong>, we are committed to providing a
                welcoming environment for everyone. We also help eligible patients access care
                through the{" "}
                <Link
                  to="/services/cdcp-dentist-pickering-on"
                  className="text-primary font-semibold hover:underline"
                >
                  CDCP
                </Link>{" "}
                (Canadian Dental Care Plan) so cost is one less barrier to a healthy smile.
              </p>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                Family Dentist vs General Dentist: What Families Look For
              </h2>
              <div className="overflow-x-auto rounded-2xl border border-border my-6">
                <table className="w-full text-left text-base">
                  <thead className="bg-accent/60">
                    <tr>
                      <th className="px-4 py-3 font-bold text-ink">Category</th>
                      <th className="px-4 py-3 font-bold text-ink">Family Dentist</th>
                      <th className="px-4 py-3 font-bold text-ink">General Dentist</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr><td className="px-4 py-3 font-semibold">Age Range</td><td className="px-4 py-3 text-ink/80">Typically provides care for children, teens, adults, and seniors</td><td className="px-4 py-3 text-ink/80">May focus more broadly on adult or general care</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Family Convenience</td><td className="px-4 py-3 text-ink/80">Multiple family members can often be seen under one clinic</td><td className="px-4 py-3 text-ink/80">May not always emphasize household-wide coordination</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Prevention</td><td className="px-4 py-3 text-ink/80">Focuses on long-term habits and early detection across life stages</td><td className="px-4 py-3 text-ink/80">Provides preventive care based on patient needs</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Patient Experience</td><td className="px-4 py-3 text-ink/80">Often emphasizes comfort for children, parents, and anxious patients</td><td className="px-4 py-3 text-ink/80">Patient experience varies by clinic</td></tr>
                    <tr><td className="px-4 py-3 font-semibold">Continuity of Care</td><td className="px-4 py-3 text-ink/80">Tracks oral health changes over time for different family members</td><td className="px-4 py-3 text-ink/80">Tracks individual patient history</td></tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                FAQs About Family Dentistry
              </h2>
              <div className="my-6">
                <Accordion type="single" collapsible className="space-y-3">
                  {faqs.map((f) => (
                    <AccordionItem
                      key={f.q}
                      value={f.q}
                      className="rounded-xl border border-border bg-card overflow-hidden px-0 shadow-[var(--shadow-card)]"
                    >
                      <AccordionTrigger className="px-5 py-4 text-lg font-semibold text-ink hover:no-underline text-left">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-4 text-muted-foreground leading-relaxed text-base">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-ink mt-12 leading-tight tracking-tight">
                Book Family Dental Care in Pickering
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                <Link to="/" className="text-primary font-semibold hover:underline">Bayly Dental Clinic</Link> provides family dentistry for children, teens, adults, seniors,
                new patients, and long-term patients. Whether your family needs routine checkups,
                preventive care, or support with changing dental needs, our team focuses on clear
                communication and personalized care. If you are looking for a{" "}
                <strong>dental clinic near me</strong> or a <strong>dentist near me</strong> that
                can accommodate your whole household, we are here to help.
              </p>
              <ul className="space-y-4 my-6">
                {[
                  <><strong>Care for All Ages:</strong> Children, teens, adults, and seniors can receive high-quality dental care in one welcoming clinic.</>,
                  <><strong>Coordinated Family Visits:</strong> Preventive care, follow-ups, and individual treatment plans can be smoothly organized around your household's busy schedule.</>,
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="mt-1 flex-shrink-0 w-6 h-6 inline-flex items-center justify-center rounded-full bg-accent">
                      <Check className="w-3.5 h-3.5 text-primary" strokeWidth={3} />
                    </span>
                    <span className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-lg sm:text-xl text-muted-foreground sm:text-ink/75 leading-relaxed">
                Use the booking buttons below to schedule a family dental appointment at Bayly
                Dental Clinic and choose a time that works best for your household.
              </p>
            </div>
          </motion.article>

          <aside className="lg:self-start flex flex-col gap-5">
            <SidebarBookingForm />
            <h3 className="text-lg font-bold text-ink px-1 mt-2">Other services</h3>
            <div className="flex flex-col gap-4">
              {relatedServices.map((s) => (
                <Link
                  key={s.hash}
                  to={s.to}
                  hash={s.hash}
                  className="group bg-card rounded-2xl p-3 shadow-[var(--shadow-card)] border border-border flex gap-3 items-center transition-transform hover:-translate-y-1"
                >
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-base sm:text-sm font-bold text-ink leading-snug">{s.title}</h4>
                    <p className="mt-1 text-sm sm:text-xs text-muted-foreground leading-relaxed line-clamp-2">
                      {s.desc}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-primary shrink-0" />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <section className="section-pad">
        <div className="mx-auto px-5" style={{ maxWidth: "1120px" }}>
          <div
            className="relative overflow-hidden text-center rounded-3xl px-8 md:px-12 py-16 md:py-24 bg-cover bg-center bg-fixed"
            style={{ backgroundImage: `url(${ctaParallax})` }}
          >
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Ready to book your family's visit?
              </h2>
              <p className="mt-3 text-white/85 max-w-xl mx-auto">
                Coordinated care for every age, in one welcoming Pickering clinic.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <a
                  href="tel:+19054921234"
                  className="btn-primary !bg-white !text-primary-dark"
                  style={{ background: "white", color: "var(--primary-dark)" }}
                >
                  <Phone className="h-4 w-4" /> Call (905) 492-1234
                </a>
                <BookAppointmentLink
                  className="btn-primary"
                  style={{ background: "transparent", border: "1.5px solid white", color: "white" }}
                >
                  <Calendar className="h-4 w-4" /> Book Appointment
                </BookAppointmentLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WelcomeVideo />
      <Testimonials />
      <Appointment />
    </SiteLayout>
  );
}